/**
 * Regex Detector
 *
 * Detects JavaScript regular expression literals like /pattern/flags.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITypeDetector } from '@/interfaces/ITypeDetector';

function findClosingSlash(literal: string): number {
  // Scan from index 1 and find the first unescaped '/' that is not inside a character class.
  let escaped = false;
  let inCharClass = false;

  for (let i = 1; i < literal.length; i++) {
    const ch = literal[i];

    if (escaped) {
      escaped = false;
      continue;
    }

    if (ch === '\\') {
      escaped = true;
      continue;
    }

    if (ch === '[') {
      inCharClass = true;
      continue;
    }

    if (ch === ']' && inCharClass) {
      inCharClass = false;
      continue;
    }

    if (ch === '/' && !inCharClass) {
      return i;
    }
  }

  return -1;
}

function isValidFlags(flags: string): boolean {
  // JS flags as of modern runtimes: d g i m s u v y
  // Allow empty; disallow whitespace and duplicates.
  if (!/^[dgimsuvy]*$/.test(flags)) return false;
  const set = new Set(flags.split(''));
  return set.size === flags.length;
}

export const RegexDetector: ITypeDetector = {
  id: 'regex-detector',
  name: 'Regex Detector',
  priority: 78,
  detectedType: SEMANTIC_TYPES.REGEX,

  detect(input: string) {
    const text = input.trim();
    if (!text.startsWith('/')) return null;

    const closeIndex = findClosingSlash(text);
    if (closeIndex <= 1) return null;

    const pattern = text.slice(1, closeIndex);
    const flags = text.slice(closeIndex + 1);

    if (!isValidFlags(flags)) return null;

    try {
      // Validate the regex is actually constructible.
      // Note: this also prevents false-positives on random strings.
      // eslint-disable-next-line no-new -- side-effect validation: RegExp constructor tests pattern validity
      new RegExp(pattern, flags);
      return SEMANTIC_TYPES.REGEX;
    } catch {
      return null;
    }
  },
};
